import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { client } from 'src/enviroment/api/axios'
import { handleApiError } from 'src/enviroment/api/errorHandler'
import { useTenantStore } from 'src/enviroment/store/tenentStore'
import { STORAGE_KEYS } from 'src/enviroment/constants/storageKeys'
import { enqueueSnackbar } from 'notistack'
import { Member } from 'src/templates/members/columns'
import { UserRole } from 'src/types/user'
import { userRouterKeys } from './user'
import { Mutation, Query } from '../index'

export const memberRouterKeys = {
  list: 'member.list',
} as const

import { DEFAULT_TAKE } from 'src/enviroment/constants/constants'

export interface CreateMemberBody {
  email: string
  role: UserRole
  projectIds?: string[]
}

export interface EditMemberBody {
  id: string
  firstName?: string
  lastName?: string
  role?: UserRole
  projectIds?: string[]
}

export interface UpdateMemberRoleBody {
  userId: string
  role: UserRole
}

export interface UpdateRoleResponse {
  status: number
  data: {
    message: string
  }
}

export interface MutationContext {
  previousData: InfiniteData<Member[]> | undefined
}

export const memberRouter = {
  list: {
    useInfiniteQuery: () => {
      const queryClient = useQueryClient()
      const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useInfiniteQuery({
        queryKey: [memberRouterKeys.list],
        queryFn: async ({ pageParam: cursorId }) => {
          return (
            await client.get<Member[]>(`/tenants/${currentTenantId}/members`, {
              params: { cursorId, take: DEFAULT_TAKE },
            })
          ).data
        },
        enabled: Boolean(queryClient.getQueryData([userRouterKeys.currentUser])),
        getNextPageParam: (lastPage) => lastPage.at(-1)?.id,
      })
    },
  },

  create: {
    useMutation: (opts?: Mutation<CreateMemberBody, void>) => {
      const queryClient = useQueryClient()
      const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        ...opts,
        mutationFn: async (data: CreateMemberBody) => {
          await client.post(`/tenants/${currentTenantId}/members`, data)
        },
        onSuccess: (...args) => {
          enqueueSnackbar('Member invited successfully', { variant: 'success' })
          opts?.onSuccess?.(...args)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
        onSettled: (...args) => {
          localStorage.removeItem(STORAGE_KEYS.INVITE_TOKEN)
          queryClient.invalidateQueries({ queryKey: [memberRouterKeys.list] })
          opts?.onSettled?.(...args)
        },
      })
    },
  },

  edit: {
    useMutation: (opts?: Mutation<EditMemberBody, void>) => {
      const queryClient = useQueryClient()
      const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        ...opts,
        mutationFn: async ({ id, ...data }: EditMemberBody) => {
          await client.patch(`/tenants/${currentTenantId}/members/${id}`, data)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
        onSettled: (...args) => {
          queryClient.invalidateQueries({ queryKey: [memberRouterKeys.list] })
          opts?.onSettled?.(...args)
        },
      })
    },
  },

  updateRole: {
    useMutation: (opts?: Mutation<UpdateMemberRoleBody, UpdateRoleResponse>) => {
      const queryClient = useQueryClient()
      const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation<UpdateRoleResponse, Error, UpdateMemberRoleBody, MutationContext>({
        ...opts,
        mutationFn: ({ userId, role }) => {
          return client.patch(`/tenants/${currentTenantId}/members/${userId}/role`, { role })
        },
        onMutate: async ({ userId, role }) => {
          await queryClient.cancelQueries({ queryKey: [memberRouterKeys.list] })

          const previousData = queryClient.getQueryData<InfiniteData<Member[]>>([memberRouterKeys.list])

          queryClient.setQueryData<InfiniteData<Member[]>>([memberRouterKeys.list], (old) => {
            if (!old) return { pages: [], pageParams: [] }
            return {
              ...old,
              pages: old.pages.map((page: Member[]) =>
                page.map((member: Member) => (member.id === userId ? { ...member, role } : member)),
              ),
            }
          })

          return { previousData }
        },
        onError: (error, variables, context) => {
          if (context?.previousData) {
            queryClient.setQueryData<InfiniteData<Member[]>>([memberRouterKeys.list], context.previousData)
          }
          handleApiError(error)
          opts?.onError?.(error, variables, context)
        },
        onSettled: (...args) => {
          queryClient.invalidateQueries({ queryKey: [memberRouterKeys.list] })
          opts?.onSettled?.(...args)
        },
      })
    },
  },

  delete: {
    useMutation: (opts?: Mutation<string, void>) => {
      const queryClient = useQueryClient()
      const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        ...opts,
        mutationFn: async (userId: string) => {
          await client.delete(`/tenants/${currentTenantId}/members/${userId}`)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
        onSettled: (...args) => {
          queryClient.invalidateQueries({ queryKey: [memberRouterKeys.list] })
          opts?.onSettled?.(...args)
        },
      })
    },
  },

  deleteAll: {
    useMutation: (opts?: Mutation<void, void>) => {
      const queryClient = useQueryClient()
      const currentTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        ...opts,
        mutationFn: async () => {
          await client.delete(`/tenants/${currentTenantId}/members`)
        },
        onError: (...args) => {
          handleApiError(args[0])
          opts?.onError?.(...args)
        },
        onSettled: (...args) => {
          queryClient.invalidateQueries({ queryKey: [memberRouterKeys.list] })
          opts?.onSettled?.(...args)
        },
      })
    },
  },
}
